import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { SupportEvent, SupportTicket } from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import { getSDK } from '../services/sdk-instance';
import { PageShell } from '../components/layout/PageShell';
import { usePublishViewSnapshot } from '../components/shogun/usePublishViewSnapshot';
import { useSupportLiveRefresh } from '../hooks/useSupportLiveRefresh';

const PAGE = 50;

function encodeCursor(next: { last_activity_at: string; id: string }): string {
  return `${next.last_activity_at}|${next.id}`;
}

export function SupportInbox({
  fixtureTickets,
  operator,
  operatorSettled,
  operatorError,
  live,
  userId,
}: {
  fixtureTickets?: SupportTicket[];
  operator: boolean;
  operatorSettled: boolean;
  operatorError: unknown;
  live: boolean;
  userId?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [audience, setAudience] = useState('platform');
  const [thisAccount, setThisAccount] = useState(false);
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>(fixtureTickets ?? []);
  const [next, setNext] = useState<{ last_activity_at: string; id: string } | undefined>();
  const [listError, setListError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(
    async (cursor?: string, replace = false) => {
      if (!live) return;
      setLoading(true);
      try {
        const params = {
          search: search.trim() === '' ? undefined : search.trim(),
          status: status === '' ? undefined : (status as SupportTicket['status']),
          audience: operator
            ? (audience as SupportTicket['audience'] | 'all')
            : undefined,
          this_account: operator && thisAccount ? true : undefined,
          assigned_to_me: operator && assignedToMe ? true : undefined,
          limit: PAGE,
          cursor,
        };
        const page = operator
          ? await getSDK().support.adminList(params)
          : await getSDK().support.list(params);
        setTickets((prev) => (replace ? page.tickets : [...prev, ...page.tickets]));
        setNext(page.next);
        setListError(null);
      } catch (err) {
        setListError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [assignedToMe, audience, live, operator, search, status, thisAccount],
  );

  useEffect(() => {
    if (fixtureTickets !== undefined) {
      setTickets(fixtureTickets);
      setNext(undefined);
      return;
    }
    if (live && !operatorSettled) {
      return;
    }
    void loadPage(undefined, true);
  }, [fixtureTickets, live, loadPage, operatorSettled]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void loadPage(undefined, true);
        void queryClient.invalidateQueries({ queryKey: ['support', 'unseen'] });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadPage, queryClient]);

  const onLiveEvent = useCallback(
    (_event: SupportEvent) => {
      void loadPage(undefined, true);
      void queryClient.invalidateQueries({ queryKey: ['support', 'unseen'] });
    },
    [loadPage, queryClient],
  );
  useSupportLiveRefresh(onLiveEvent);

  useEffect(() => {
    const scroller = document.querySelector('[data-testid="page-shell-scroll"]');
    if (!(scroller instanceof HTMLElement) || !live) return;
    let busy = false;
    const onScroll = () => {
      if (busy || next === undefined) return;
      const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (remaining > 80) return;
      busy = true;
      void loadPage(encodeCursor(next)).finally(() => {
        busy = false;
      });
    };
    scroller.addEventListener('scroll', onScroll);
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [live, loadPage, next]);

  const snapshot = useMemo(
    () => ({
      route: '/support',
      context: {
        surface: 'support',
        primary_type: 'support_ticket',
        counts: [{ lane: 'inbox', total: tickets.length }],
        visible: tickets.map((t) => ({
          type: 'support_ticket',
          id: t.id,
          label: t.display_id,
          state: t.status,
        })),
      },
      ...(tickets.length === 0
        ? { starterPrompts: ['I need to file a support ticket about…'] }
        : {}),
    }),
    [tickets],
  );
  usePublishViewSnapshot(snapshot);

  return (
    <PageShell
      title="Support"
      description={
        operator
          ? 'Platform support queue. File and update cases in the chat below.'
          : 'Your account’s cases. File a ticket by chatting with Support below.'
      }
      headerSlot={
        <div className="flex flex-wrap items-end gap-3" data-testid="support-inbox-filters">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Search
            <input
              data-testid="support-search"
              className="h-9 rounded-md bg-muted px-2 text-sm text-foreground"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Status
            <select
              data-testid="support-filter-status"
              className="h-9 rounded-md bg-muted px-2 text-sm text-foreground"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="open">open</option>
              <option value="pending_response">pending_response</option>
              <option value="pending">pending</option>
              <option value="resolve">resolve</option>
              <option value="closed">closed</option>
            </select>
          </label>
          {operator ? (
            <>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Audience
                <select
                  data-testid="support-filter-audience"
                  className="h-9 rounded-md bg-muted px-2 text-sm text-foreground"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  <option value="platform">platform</option>
                  <option value="account">account</option>
                  <option value="all">all</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="support-filter-this-account"
                  checked={thisAccount}
                  onChange={(e) => setThisAccount(e.target.checked)}
                />
                This account
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="support-filter-assigned-to-me"
                  checked={assignedToMe}
                  onChange={(e) => setAssignedToMe(e.target.checked)}
                />
                Assigned to me
              </label>
            </>
          ) : null}
        </div>
      }
    >
      <div
        data-testid="support-inbox"
        data-operator={operator ? 'true' : 'false'}
        data-operator-settled={operatorSettled ? 'true' : 'false'}
      >
        {operatorError instanceof Error ? (
          <p className="text-sm text-destructive" role="alert" data-testid="support-operator-error">
            {operatorError.message}
          </p>
        ) : null}
        {listError ? (
          <p className="text-sm text-destructive" role="alert">
            {listError}
          </p>
        ) : null}
        {tickets.length === 0 && !loading && operatorSettled ? (
          <div data-testid="support-empty-state" className="space-y-2 text-sm text-muted-foreground">
            <p>No support tickets yet.</p>
            <p>File a ticket by chatting with Support below. There is no form on this page.</p>
          </div>
        ) : (
          <ol data-testid="support-ticket-list" className="space-y-2">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <button
                  type="button"
                  data-testid={`support-ticket-row-${ticket.display_id}`}
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left hover:bg-accent"
                  onClick={() => navigate(`/support/${ticket.display_id}`)}
                >
                  <span className="min-w-0">
                    <span className="font-medium">{ticket.display_id}</span>
                    <span className="ml-2 text-sm">{ticket.title}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    {ticket.unseen ? <Badge data-testid="support-row-unseen">unseen</Badge> : null}
                    {operator && ticket.assignee_user_id === userId ? (
                      <Badge>assigned to me</Badge>
                    ) : null}
                    <Badge>{ticket.status}</Badge>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </PageShell>
  );
}
